import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
	SessionsCardSkeleton,
} from "./DashboardSkeletons";
import { dashboardCardGroups } from "./utils/dashboardUtils";
import { Translate } from "@/app/application/Translate";
import { getCardBorderStyle } from "@/app/application/theme";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import { ArchiveDashboard } from "@/features/training/pages/Dashboard/ArchiveDashboard";
import KpiStatisticsDataGridDashboard from "@/features/training/pages/Dashboard/KpiStatisticsDataGridDashboard";
import SessionsDataGrid from "@/features/training/pages/Dashboard/SessionsUserDashboard";
import { TrainingDashboardTourSteps } from "@/features/training/pages/Dashboard/TrainingDashboardTourSteps";
import { UpcomingSessionsDashboard } from "@/features/training/pages/Dashboard/UpcomingSessionsDashboard";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import {
	Box,
	Card,
	CardContent,
	Chip,
	LinearProgress,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTour } from "@reactour/tour";
import { useIsFetching } from "@tanstack/react-query";

import { QuickRangeToggle } from "@/components/date-range/QuickRangeToggle";
import {
	CanonicalQuickRangeKey,
	shiftDateByQuickRange,
} from "@/components/date-range/quickRange";

import { useFetchTrainingUsersWithMissedSessions } from "@/service/TrainingQueries";

const PAGE_PADDING = { px: { xs: 2, sm: 2.5 }, py: 0.5 };
const KPI_RANGE_OPTIONS = [
	{ value: "3m", label: "3m" },
	{ value: "6m", label: "6m" },
	{ value: "1y", label: "1y" },
] as const;
const RANGE_LABELS: Record<CanonicalQuickRangeKey, string> = {
	"3m": "Last 3 months",
	"6m": "Last 6 months",
	"1y": "Last year",
};
const formatFollowUpSessionsLabel = (count: number) =>
	count === 1 ? "1 session needs follow-up" : `${count} sessions need follow-up`;

const getPanelCardSx = (t: Theme) => ({
	borderRadius: { xs: 2, sm: 3 },
	border: getCardBorderStyle(t),
	backgroundColor: t.palette.background.paper,
	overflow: "hidden",
	boxShadow:
		t.palette.mode === "dark"
			? "0 1px 0 0 rgba(255,255,255,0.04)"
			: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
});

const subtleSurfaceSx = (t: Theme, color: string) => ({
	bgcolor: alpha(color, t.palette.mode === "dark" ? 0.14 : 0.08),
	color,
});

const statTileSx = (t: Theme) => ({
	borderRadius: 2,
	border: `1px solid ${alpha(t.palette.divider, 0.75)}`,
	backgroundColor:
		t.palette.mode === "dark"
			? alpha(t.palette.common.white, 0.015)
			: alpha(t.palette.primary.main, 0.02),
	px: 1.25,
	py: 1,
});

const scrollbarSx = (t: Theme) => ({
	"&::-webkit-scrollbar": { width: 6 },
	"&::-webkit-scrollbar-track": { background: "transparent" },
	"&::-webkit-scrollbar-thumb": {
		borderRadius: 3,
		background:
			t.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
	},
	"&::-webkit-scrollbar-thumb:hover": {
		background:
			t.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
	},
});

// Reusable Card Wrapper – aligned with archive/procedure panels
const DashboardCard = ({
	children,
	...rest
}: { children: React.ReactNode } & React.ComponentProps<typeof Card>) => {
	const theme = useTheme();

	return (
		<Card
			variant="outlined"
			{...rest}
			sx={{
				...getPanelCardSx(theme),
				height: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<CardContent
				sx={{
					p: 0,
					"&:last-child": { pb: 0 },
					minWidth: "100%",
					flex: 1,
					display: "flex",
					flexDirection: "column",
				}}
			>
				{children}
			</CardContent>
		</Card>
	);
};

// KPI Card Component
const KPICard = ({
	selectedRange,
	dateRange,
	onRangeChange,
}: {
	selectedRange: CanonicalQuickRangeKey;
	dateRange: { startDate: Date; endDate: Date };
	onRangeChange: (range: CanonicalQuickRangeKey) => void;
}) => {
	const navigate = useNavigate();
	const theme = useTheme();

	return (
		<DashboardCard>
			<Box
				data-tour="training-kpi-controls"
				sx={{
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					alignItems: { xs: "flex-start", sm: "center" },
					gap: 2,
					justifyContent: "space-between",
					p: 2.25,
					borderRadius: 2,
					bgcolor: (t) =>
						t.palette.mode === "light"
							? alpha(t.palette.primary.main, 0.025)
							: alpha(t.palette.primary.main, 0.08),
					border: (t) => getCardBorderStyle(t),
				}}
			>
				<Box>
					<Typography
						variant="overline"
						sx={{
							display: "block",
							mb: 0.15,
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Training.Dashboard.KPI">
							KPI key performance indicators
						</Translate>
					</Typography>
					<Typography
						variant="h6"
						sx={{ fontWeight: 800, lineHeight: 1.1, mb: 0.35 }}
					>
						<Translate String="Training.Dashboard.PerformanceOverview">
							Performance overview
						</Translate>
					</Typography>
					<Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
						<Chip
							size="small"
							label={RANGE_LABELS[selectedRange]}
							sx={{
								height: 28,
								borderRadius: 2,
								fontWeight: 800,
								...subtleSurfaceSx(theme, theme.palette.primary.main),
							}}
						/>
						<Chip
							size="small"
							label={`${dateRange.startDate.getFullYear()}-${dateRange.endDate.getFullYear()}`}
							sx={{
								height: 28,
								borderRadius: 2,
								fontWeight: 800,
								...subtleSurfaceSx(
									theme,
									theme.palette.mode === "dark"
										? theme.palette.info.light
										: theme.palette.info.dark,
								),
							}}
						/>
					</Stack>
				</Box>

				<Stack
					direction="row"
					alignItems="center"
					spacing={1}
					flexWrap="wrap"
					sx={{ mt: { xs: 0.75, sm: 0 } }}
				>
					<Box
						sx={(t) => ({
							display: "inline-flex",
							alignItems: "center",
							gap: 1,
							px: 1,
							py: 0.75,
							borderRadius: 2,
							border: "1px solid",
							borderColor: alpha(t.palette.primary.main, 0.5),
							bgcolor: alpha(t.palette.primary.main, 0.06),
						})}
					>
						<Typography
							variant="body2"
							sx={{
								color: "primary.main",
								fontWeight: 600,
								whiteSpace: "nowrap",
							}}
						>
							<Translate String="Auto.Features.Training.Pages.Dashboard.TrainingDashboard.L242C8.QuickRange">Quick range</Translate>
						</Typography>

						<QuickRangeToggle
							value={selectedRange}
							onChange={onRangeChange}
							options={KPI_RANGE_OPTIONS}
							ariaLabel="KPI date range"
							groupProps={{
								sx: {
									"& .MuiToggleButton-root": {
										minWidth: 44,
										px: 1,
									},
								},
							}}
						/>
					</Box>

					<ProcedureButton
						variant="outlined"
						color="primary"
						size="small"
						startIcon={<LaunchIcon />}
						onClick={() => navigate("/training/archive")}
						sx={{
							borderRadius: 2,
							fontWeight: 700,
							textTransform: "none",
							px: 2,
							py: 0.75,
						}}
					>
						<Translate String="Training.Dashboard.KPI.GoToKPI">
							Go to KPI
						</Translate>
					</ProcedureButton>
				</Stack>
			</Box>

			<Box
				data-tour="training-kpi-grid"
				sx={{
					px: { xs: 1.5, sm: 2.25 },
					pb: { xs: 1.5, sm: 2.25 },
				}}
			>
				<KpiStatisticsDataGridDashboard
					startDate={dateRange.startDate}
					endDate={dateRange.endDate}
					selectedRange={selectedRange}
					onRangeChange={onRangeChange}
				/>
			</Box>
		</DashboardCard>
	);
};

// Main Dashboard
const TrainingDashboard = () => {
	const theme = useTheme();
	const { setSteps, setCurrentStep } = useTour();
	const { data, isLoading } = useFetchTrainingUsersWithMissedSessions();
	const [selectedRange, setSelectedRange] =
		useState<CanonicalQuickRangeKey>("3m");
	const [dateRange, setDateRange] = useState(() => {
		const endDate = new Date();
		const startDate = shiftDateByQuickRange("3m", endDate);
		return { startDate, endDate };
	});

	useEffect(() => {
		setCurrentStep(0);
		if (setSteps) setSteps(TrainingDashboardTourSteps);
	}, [setSteps, setCurrentStep]);

	const handleDateRangeChange = (range: CanonicalQuickRangeKey) => {
		const endDate = new Date();
		const startDate = shiftDateByQuickRange(range, endDate);
		setDateRange({ startDate, endDate });
		setSelectedRange(range);
	};

	const dashboardQueryKeys = [
		"participantsWithMissedSessions",
		"participants",
		"courseSessions",
		"drillSessions",
		"kpiStatistics",
	];
	const isAnyTrainingFetching = useIsFetching({
		predicate: (query) => {
			const key = query.queryKey[0];
			return typeof key === "string" && dashboardQueryKeys.includes(key);
		},
	});
	const missedUsers = data?.Items?.length ?? 0;
	const missedSessions =
		data?.Items?.reduce((sum, item) => sum + (item.Sessions?.length ?? 0), 0) ?? 0;
	const summaryItems = [
		{ label: "Selected range", value: RANGE_LABELS[selectedRange] },
		{ label: "Emergency personnel", value: String(missedUsers) },
	];
	const renderDashboardCard = (
		config: (typeof dashboardCardGroups)[number]["cards"][number],
	) => (
		<DashboardCard
			key={`${config.type}-${config.data ?? config.trainingType ?? "default"}`}
		>
			{config.type === "archive" ? (
				<ArchiveDashboard
					initialTrainingType={config.trainingType}
					name={config.name}
				/>
			) : (
				<UpcomingSessionsDashboard
					type={config.type}
					data={config.data ?? "upcoming"}
					name={config.name}
				/>
			)}
		</DashboardCard>
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minHeight: 0,
				minWidth: 0,
				overflow: "hidden",
				backgroundColor: "background.default",
			}}
		>
			{isAnyTrainingFetching > 0 && (
				<LinearProgress
					sx={{
						position: "sticky",
						top: 0,
						left: 0,
						right: 0,
						zIndex: 1100,
					}}
				/>
			)}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					...PAGE_PADDING,
					...scrollbarSx(theme),
				}}
			>
				<Box
					sx={{
						mx: "auto",
						width: "100%",
						minWidth: 0,
					}}
				>
					<Stack spacing={2}>
						<Card
							variant="outlined"
							sx={{
								...getPanelCardSx(theme),
								borderColor: alpha(theme.palette.primary.main, 0.14),
							}}
						>
							<CardContent
								sx={{
									p: { xs: 2, sm: 2.5 },
									"&:last-child": { pb: { xs: 2, sm: 2.5 } },
									mb: 2,
								}}
							>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: {
											xs: "1fr",
											lg: "minmax(0, 1.3fr) minmax(320px, 0.7fr)",
										},
										gap: 2,
										alignItems: "start",
									}}
								>
									<Box sx={{ minWidth: 0 }}>
										<Stack
											direction="row"
											spacing={0.75}
											alignItems="center"
											sx={{ mb: 0.5 }}
										>
											<DashboardOutlinedIcon
												sx={{ fontSize: 18, color: "primary.main" }}
											/>
											<Typography
												variant="caption"
												sx={{
													fontWeight: 700,
													color: "primary.main",
													letterSpacing: "0.06em",
													textTransform: "uppercase",
												}}
											>
												<Translate String="Auto.Features.Training.Pages.Dashboard.TrainingDashboard.L451C13.TrainingDashboard">Training dashboard</Translate>
											</Typography>
										</Stack>

										<Typography
											variant="h4"
											component="h1"
											sx={{
												fontWeight: 700,
												letterSpacing: "-0.02em",
												lineHeight: 1.1,
												fontSize: { xs: "1.4rem", sm: "1.8rem" },
											}}
										>
											<Translate String="Training.Dashboard.Title">
												Training overview
											</Translate>
										</Typography>
										<Box
											sx={{
												display: "flex",
												flexWrap: "wrap",
												gap: 0.75,
												mt: 1.5,
											}}
										>
											<Chip
												size="small"
												label={RANGE_LABELS[selectedRange]}
												sx={{
													height: 28,
													borderRadius: 2,
													fontWeight: 800,
													...subtleSurfaceSx(
														theme,
														theme.palette.primary.main,
													),
												}}
											/>
											<Chip
												size="small"
												label={formatFollowUpSessionsLabel(missedSessions)}
												sx={{
													height: 28,
													borderRadius: 2,
													fontWeight: 800,
													...subtleSurfaceSx(
														theme,
														theme.palette.mode === "dark"
															? theme.palette.warning.light
															: theme.palette.warning.dark,
													),
												}}
											/>
										</Box>
									</Box>

									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: {
												xs: "1fr",
												sm: "repeat(2, minmax(0, 1fr))",
											},
											gap: 1,
										}}
									>
										{summaryItems.map((item) => (
											<Box key={item.label} sx={statTileSx(theme)}>
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{
														fontWeight: 700,
														textTransform: "uppercase",
														letterSpacing: "0.04em",
													}}
												>
													{item.label}
												</Typography>
												<Typography
													variant="subtitle2"
													sx={{
														fontWeight: 800,
														mt: 0.2,
														lineHeight: 1.2,
													}}
												>
													{item.value}
												</Typography>
											</Box>
										))}
									</Box>
								</Box>
							</CardContent>
						</Card>

						<KPICard
							selectedRange={selectedRange}
							dateRange={dateRange}
							onRangeChange={handleDateRangeChange}
						/>

						<Box data-tour="training-upcoming-archive">
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: {
											xs: "1fr",
											md: "repeat(2, minmax(0, 1fr))",
										},
										gap: 2.25,
										alignItems: "start",
									}}
							>
								{dashboardCardGroups.map((group) => (
									<Box key={group.key} sx={{ minWidth: 0 }}>
										<Box sx={{ mb: 1.1, px: 0.25 }}>
											<Typography
												variant="overline"
												sx={{
													display: "block",
													color: "primary.main",
													fontWeight: 700,
													letterSpacing: "0.08em",
													textTransform: "uppercase",
												}}
											>
												{group.title}
											</Typography>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ lineHeight: 1.35 }}
											>
												{group.description}
											</Typography>
										</Box>

										<Stack spacing={2}>
											{group.cards.map(renderDashboardCard)}
										</Stack>
									</Box>
								))}
							</Box>
						</Box>

						<Box data-tour="training-missed">
							{isLoading ? (
								<SessionsCardSkeleton />
							) : (
								<SessionsDataGrid items={data?.Items ?? []} isLoading={false} />
							)}
						</Box>
					</Stack>
				</Box>
			</Box>
		</Box>
	);
};

export default TrainingDashboard;
